import { DirectoriesType, slugify } from "@/types/directory";
import { Genre, ProJobTitle, City } from "@/types/user";

class DirectoryService {
  public getDirectories(): DirectoriesType {
    const directories: DirectoriesType = {};

    Object.values(ProJobTitle).forEach((jobSlug) => {
      directories[slugify(jobSlug)] = {
        title: `Connect with Top ${jobSlug}s`,
        breadcrumbs: [
          {
            title: `${jobSlug}s`,
            link: `/industry-pros/${slugify(jobSlug)}`,
          },
        ],
        metaTitle: `Connect with Top ${jobSlug}s for Personalized Feedback on Your Music & Career`,
        metaDescription: `Looking for feedback on your music & career? Connect with top ${jobSlug}s through Xposure Music and get the guidance and support you need to succeed.`,
        links1: Object.values(ProJobTitle)
          .filter((slug) => slug != slugify(jobSlug))
          .map((slug) => ({
            title: `${slug}`,
            link: `/industry-pros/${slugify(slug)}`,
          })),
        links2: Object.values(City).map((slug) => ({
          title: `${slug}`,
          link: `/industry-pros/${slugify(slug)}`,
        })),
        detailTitle: `Connect with Top ${jobSlug}s for Personalized Feedback on Your Music & Career`,
        detailDescription: `Looking for feedback on your music & career? Connect with top ${jobSlug}s through Xposure Music and get the guidance and support you need to succeed.`,
        query: {
          job: jobSlug,
        },
      };

      Object.values(Genre).forEach((genre) => {
        directories[slugify(jobSlug)].children = {
          ...directories[slugify(jobSlug)].children,
          [slugify(genre)]: {
            title: `Connect with Top ${genre} ${jobSlug}s`,
            breadcrumbs: [
              {
                title: `${jobSlug}s`,
                link: `/industry-pros/${slugify(jobSlug)}`,
              },
              {
                title: `${genre}`,
                link: `/industry-pros/${slugify(genre)}`,
              },
            ],
            metaTitle: `Find the Best ${genre} ${jobSlug}s to Help Advance Your Music Career`,
            metaDescription: `Need feedback on your music? Connect with the best ${genre} ${jobSlug}s in the industry and get the guidance and support you need to succeed.`,
            links1: Object.values(ProJobTitle)
              .filter((slug) => slug != slugify(jobSlug))
              .map((slug) => ({
                title: `${genre} ${slug}`,
                link: `/industry-pros/${slugify(slug)}/${slugify(genre)}`,
              })),
            links2: Object.values(City).map((slug) => ({
              title: `${jobSlug}s in ${slug}`,
              link: `/industry-pros/${slugify(jobSlug)}/${slugify(slug)}`,
            })),
            detailTitle: `Find the Best ${genre} ${jobSlug}s to Help Advance Your Music Career`,
            detailDescription: `Need feedback on your music? Connect with the best ${genre} ${jobSlug}s in the industry and get the guidance and support you need to succeed.`,
            query: {
              job: jobSlug,
              genre: genre,
            },
          },
        };
      });

      Object.values(City).forEach((city) => {
        directories[slugify(jobSlug)].children = {
          ...directories[slugify(jobSlug)].children,
          [slugify(city)]: {
            title: `Connect with Top ${jobSlug}s in ${city}`,
            breadcrumbs: [
              {
                title: `${jobSlug}s`,
                link: `/industry-pros/${slugify(jobSlug)}`,
              },
              {
                title: `${city}`,
                link: `/industry-pros/${slugify(city)}`,
              },
            ],
            metaTitle: `Get Connected with Top ${jobSlug}s in ${city}`,
            metaDescription: `Find the best ${jobSlug}s in ${city} to guide you to success. Connect with them today and unlock your potential in the music industry.`,
            links1: Object.values(ProJobTitle)
              .filter((slug) => slug != slugify(jobSlug))
              .map((slug) => ({
                title: `${slug}s in ${city}`,
                link: `/industry-pros/${slugify(slug)}/${slugify(city)}`,
              })),
            links2: Object.values(Genre).map((slug) => ({
              title: `${slug} ${jobSlug}s`,
              link: `/industry-pros/${slugify(jobSlug)}/${slugify(slug)}`,
            })),
            detailTitle: `Get Connected with Top ${jobSlug}s in ${city}`,
            detailDescription: `Find the best ${jobSlug}s in ${city} to guide you to success. Connect with them today and unlock your potential in the music industry.`,
            query: {
              job: jobSlug,
              city: city,
            },
          },
        };
      });
    });

    Object.values(Genre).forEach((genreSlug) => {
      directories[slugify(genreSlug)] = {
        title: `Connect with Top ${genreSlug} Music Professionals`,
        breadcrumbs: [
          {
            title: `${genreSlug}`,
            link: `/industry-pros/${slugify(genreSlug)}`,
          },
        ],
        metaTitle: `Connect with ${genreSlug} Music Professionals to Get Expert Feedback on Your Music`,
        metaDescription: `Connect with top ${genreSlug} music professionals through Xposure Music to get feedback on your music career. Improve your skills, get expert advice, and take your career to the next level.`,
        links1: Object.values(Genre)
          .filter((slug) => slug != slugify(genreSlug))
          .map((slug) => ({
            title: `${slug}`,
            link: `/industry-pros/${slugify(slug)}`,
          })),
        links2: Object.values(City).map((slug) => ({
          title: `${slug}`,
          link: `/industry-pros/${slugify(slug)}`,
        })),
        detailTitle: `Connect with ${genreSlug} Music Professionals to Get Expert Feedback on Your Music`,
        detailDescription: `Connect with top ${genreSlug} music professionals through Xposure Music to get feedback on your music career. Improve your skills, get expert advice, and take your career to the next level.`,
        query: {
          genre: genreSlug,
        },
      };
    });

    Object.values(City).forEach((citySlug) => {
      directories[slugify(citySlug)] = {
        title: `Connect with Top Music Professionals in ${citySlug}`,
        breadcrumbs: [
          {
            title: `${citySlug}`,
            link: `/industry-pros/${citySlug}`,
          },
        ],
        metaTitle: `Connect with ${citySlug} Music Professionals to Get Expert Feedback on Your Music`,
        metaDescription: `Connect with top ${citySlug} music professionals through Xposure Music to get feedback on your music career. Improve your skills, get expert advice, and take your career to the next level.`,
        links1: Object.values(Genre).map((slug) => ({
          title: `${slug}`,
          link: `/industry-pros/${slugify(slug)}`,
        })),
        links2: Object.values(City)
          .filter((slug) => slug != slugify(citySlug))
          .map((slug) => ({
            title: `${slug}`,
            link: `/industry-pros/${slugify(slug)}`,
          })),
        detailTitle: `Connect with ${citySlug} Music Professionals to Get Expert Feedback on Your Music`,
        detailDescription: `Connect with top ${citySlug} music professionals through Xposure Music to get feedback on your music career. Improve your skills, get expert advice, and take your career to the next level.`,
        query: {
          city: citySlug,
        },
      };
    });

    return directories;
  }
}

export default DirectoryService;
